import { useEffect, useLayoutEffect, useState } from "react";
import { Box, Typography, TextField, InputLabel, Button, FormHelperText, CircularProgress, Modal, IconButton } from "@mui/material";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { createCompany, getCompanyDetailsById, getUserCompanyDetails, saveUserCompanyDetails, updateCompanyDetailsById, clearUserCompanyDetails } from "../../redux/actions/b2bActions";
import { getKeyValuePairs } from "../../redux/services/utils";
import MultiSelect from "../common/MultiSelect";
import { getMasterData, uploadImage } from "../../redux/actions/appActions";
import { SET_INDUSTRIES } from "../../redux/actions/actionTypes";
import { ROUTE_COMPANY_EDIT, ROUTE_COMPANY_LIST, ROUTE_COMPANY_NEW } from "../../constants/routes";
import { useNavigate, useParams } from "react-router-dom";

const labelStylestextfield = {
  transform: "none",
  position: "relative",
  top: "10px",
  fontSize: "14px",
  color: "#1F2235",
  fontWeight: "500",
};

const labelStylestextfieldsocial = {
  transform: "none",
  position: "relative",
  fontSize: "14px",
  color: "#1F2235",
  fontWeight: "500",
  marginTop: "30px",
};

const schema = yup.object().shape({
  name: yup.string().required("Company name is required"),
  foundedIn: yup.string().required("Year founded is required"),
  noOfEmployees: yup.string().required("Number of employees is required"),
  headquarter: yup.string().required("Headquarter is required"),
  description: yup.string().required("About company is required"),
  website: yup.string().notRequired(),
  twitterProfile: yup.string().notRequired(),
  linkedinProfile: yup.string().notRequired(),
  facebookProfile: yup.string().notRequired(),
  industryOperateIn: yup.array(),
  usersLimit: yup.number().required("Users limit is required").min(1, "Users limit must be at least 1").typeError("Users limit must be a valid number"),
});

const TextInputField = ({ name, label, control, placeholder, isTextArea = false, disabled }: any) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}>
    <InputLabel shrink sx={labelStylestextfield}>
      {label}
    </InputLabel>
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <>
          <TextField
            {...field}
            fullWidth
            disabled={disabled??false}
            placeholder={placeholder}
            error={!!error}
            multiline={isTextArea} // Set to true for text area
            minRows={isTextArea ? 3 : 1} // Set minRows to 3 for text area, or 1 for normal input
            sx={{
              boxShadow: "0px 0px 11px 2px #4B44D41A inset",
              borderRadius: isTextArea ? "10px" : "95px",
              color: "#777777",
            }}
          />
          <FormHelperText sx={{ color: "#d32f2f", mt: -2, ml: "14px" }}>{error ? error.message : " "}</FormHelperText>
        </>
      )}
    />
  </Box>
);

const CompanyDetails = () => {
  const dispatch = useDispatch();
  const [selectedFile, setSelectedFile] = useState<File & { preview?: string } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [companyLogo, setCompanyLogo] = useState<File | null>(null);
  const [create, setCreate] = useState(false);
  const [ edit, setEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const location = window.location;
  const navigate = useNavigate();
  const { id } = useParams();
  
  const { companyDetails } = useSelector((state: any) => state.b2b);
  const { industries } = useSelector((state: any) => state.app);
  const { user } = useSelector((state: any) => state.app);
  const {
    name,
    logo,
    industryOperateIn,
    headquarter,
    foundedIn,
    noOfEmployees,
    description,
    linkedinProfile,
    website,
    facebookProfile,
    twitterProfile,
  } = companyDetails || {};
  const ajaxCallsInProgress = useSelector((state: any) => state.ajaxCallsInProgress);

  // Check if user can edit company details (admin and reviewer only)
  const canEditCompany = () => {
    const userRole = user?.user?.roleDetail?.roleName;
    const editableRoles = ["admin", "SEEVI_ADMIN", "EMPLOYER_JOB_ADMIN"];
    console.log('User role:', userRole);
    return editableRoles.includes(String(userRole || ""));
  };

  const maxWords = 500;

  // Initialize with empty values to prevent showing previous data
  const emptyDefaultValues = {
    logo:"",
    name: "",
    foundedIn: "",
    noOfEmployees: "",
    headquarter: "",
    description: "",
    website: "",
    twitterProfile: "",
    linkedinProfile: "",
    facebookProfile: "",
    industryOperateIn: [],
    usersLimit: "",
   
  };

  const { handleSubmit, control, reset, getValues } = useForm({
    resolver: yupResolver<any>(schema),
    defaultValues: emptyDefaultValues,
  });

  const wordCount = getValues("description").trim().split(/\s+/).filter(Boolean).length;

  useLayoutEffect(() => {
    dispatch(getMasterData("INDUSTRY", SET_INDUSTRIES));
    // check a router path and set create true or false
    const path = location.pathname;
    console.log('CompanyDetails - Path:', path, 'ID:', id);
    
    if(path === ROUTE_COMPANY_NEW){
     
      // create
      setCreate(true);
      setIsLoading(false);
      console.log('CompanyDetails - Setting create mode, loading false');
      // Clear company details from Redux store
      dispatch(clearUserCompanyDetails());
      // For create mode, reset form to empty values
      reset(emptyDefaultValues);
    }else if(path.startsWith('/company-edit/') || path === ROUTE_COMPANY_EDIT){
      // edit
      setEdit(true);
      setIsLoading(true);
      console.log('CompanyDetails - Setting edit mode, loading true');
      // Clear form to prevent showing previous data
      reset(emptyDefaultValues);
      // If ID is present in params, call the new API to get company details by ID
      if(id){
        dispatch(getCompanyDetailsById(parseInt(id)));
      } else {
        dispatch(getUserCompanyDetails());
      }
    }else{
      // If ID is present in params, call the new API to get company details by ID
      if(id){
        setEdit(true);
        setIsLoading(true);
        console.log('CompanyDetails - Setting edit mode (else), loading true');
        // Clear form to prevent showing previous data
        reset(emptyDefaultValues);
      //  dispatch(getCompanyDetailsById(parseInt(id)));
      } else {

        dispatch(getUserCompanyDetails());
      }
      setCreate(false);
    }
  }, [id]);


  // Reset when companyDetails updates
  useEffect(() => {

  
    if (companyDetails && !create) {
      console.log('CompanyDetails - Data received, setting loading false');
      setIsLoading(false);
      
      // Set preview URL if logo exists
      if (companyDetails.logo) {
        setPreviewUrl(companyDetails.logo);
      }
      
      reset({
        logo : "",
        name: companyDetails.name || "",
        foundedIn: companyDetails.foundedIn || "",
        noOfEmployees: companyDetails.noOfEmployees || "",
        headquarter: companyDetails.headquarter || "",
        description: companyDetails.description || "",
        website: companyDetails.website || "",
        twitterProfile: companyDetails.twitterProfile || "",
        linkedinProfile: companyDetails.linkedinProfile || "",
        facebookProfile: companyDetails.facebookProfile || "",
        industryOperateIn: getKeyValuePairs(companyDetails.industryOperateIn) || [],
        usersLimit: companyDetails.usersLimit || companyDetails.noOfEmployees || "",
      });
    }else{
      reset(emptyDefaultValues);
      setPreviewUrl(null);
    }
  }, [companyDetails, reset, create]);

  // Handle loading state when ajax calls complete (including errors)
  useEffect(() => {
    console.log('CompanyDetails - Ajax calls:', ajaxCallsInProgress, 'Loading:', isLoading);
    if (ajaxCallsInProgress === 0 && isLoading) {
      // If no ajax calls in progress and we're still loading, stop loading
      // This handles cases where API calls fail
      console.log('CompanyDetails - No ajax calls, setting loading false');
      setIsLoading(false);
    }
  }, [ajaxCallsInProgress, isLoading]);


  const onSubmit = async () => {
    const data = getValues();
    if(create){
      // upload image
      const uploadImage = await uploadFile(companyLogo);
      // create
      const payload = {
        ...data,
        logo: uploadImage??"",
        industryOperateIn: data?.industryOperateIn?.map?.((el: any) => el?.value),
      };
      const res = await dispatch(createCompany(payload));
      if(res){
        setCreate(false);
        setEdit(true);  
      //  navigate(ROUTE_COMPANY_LIST);
        navigate(-1);
        
      }
    }else if(edit){
      // edit - check if 
      // ID is present to determine which API to call
      if(id){
        // If ID is present, use the new update API
        const uploadImage = await uploadFile(companyLogo);
        console.log('CompanyDetails - Upload image:', uploadImage?.logo);
        const payload = {
          name: data.name,
          industry: data.industryOperateIn?.map?.((el: any) => el?.value)?.[0] || "",
          contactPerson: data.contactPerson || "",
          email: data.email || "",
          phoneNumber: data.phoneNumber || "",
          address: data.address || "",
          city: data.city || "",
          state: data.state || "",
          postalCode: data.postalCode || "",
          country: data.country || "",
          headquarter: data.headquarter,
          foundedIn: parseInt(data.foundedIn) || 0,
          noOfEmployees: parseInt(data.noOfEmployees) || 0,
          culture: data.culture || "",
          location: data.location || [],
          videoLink: data.videoLink || "",
          description: data.description,
          linkedinProfile: data.linkedinProfile,
          twitterProfile: data.twitterProfile,
          facebookProfile: data.facebookProfile,
          website: data.website,
          logoImagePath: uploadImage ? uploadImage?.logo : companyDetails?.logo || "",
          industryOperateIn: data?.industryOperateIn?.map?.((el: any) => el?.value) || [],
          usersLimit: parseInt(data.usersLimit) || 1,
        };

        console.log('CompanyDetails - Update company details:', payload);
        dispatch(updateCompanyDetailsById(parseInt(id), payload, {
          successFun: () => {
            // Optionally refresh the data after successful update
              dispatch(getCompanyDetailsById(parseInt(id)));
            
          }
        }));
      } else {
        // If no ID, use the existing saveUserCompanyDetails API
        const payload = {
          ...data,
          industryOperateIn: data?.industryOperateIn?.map?.((el: any) => el?.value),
        };
        dispatch(saveUserCompanyDetails(payload));
      }
    }else{
      const uploadImage = await uploadFile(companyLogo);
      console.log('CompanyDetails - Upload image:', uploadImage?.logo);
      const payload = {
        name: data.name,
        industry: data.industryOperateIn?.map?.((el: any) => el?.value)?.[0] || "",
        contactPerson: data.contactPerson || "",
        email: data.email || "",
        phoneNumber: data.phoneNumber || "",
        address: data.address || "",
        city: data.city || "",
        state: data.state || "",
        postalCode: data.postalCode || "",
        country: data.country || "",
        headquarter: data.headquarter,
        foundedIn: parseInt(data.foundedIn) || 0,
        noOfEmployees: parseInt(data.noOfEmployees) || 0,
        culture: data.culture || "",
        location: data.location || [],
        videoLink: data.videoLink || "",
        description: data.description,
        linkedinProfile: data.linkedinProfile,
        twitterProfile: data.twitterProfile,
        facebookProfile: data.facebookProfile,
        website: data.website,
        logoImagePath: uploadImage ? uploadImage?.logo : companyDetails?.logo || "",
        industryOperateIn: data?.industryOperateIn?.map?.((el: any) => el?.value) || [],
        usersLimit: parseInt(data.usersLimit) || 0,

      };

      // const payload = {
      //   ...data,
      //   industryOperateIn: data?.industryOperateIn?.map?.((el: any) => el?.value),
      // };
      dispatch(saveUserCompanyDetails(payload));

    }
  };

  const uploadFile = async (file: any) => {
    if (!file) {
      return null;
    }
    // upload image and get the url
    const res: any = await new Promise((resolve) => {
      dispatch(
        uploadImage(file, (result) => {
          resolve(result);
        })
      );
    });
    const payload = {
      logo: res?.result,
    };
    return payload;
    // dispatch(saveUserCompanyDetails(payload));
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      // Clean up previous preview URL if it exists
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Create a preview URL directly from the file (no cropping)
      const tempPreviewUrl = URL.createObjectURL(file);
      
      // Set the file directly as preview and company logo
      setSelectedFile(file);
      setPreviewUrl(tempPreviewUrl);
      setCompanyLogo(file);
      
      // Reset the input
      event.target.value = null;
    }
  };

  // Cleanup function to revoke object URLs when component unmounts
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff", // Page background color
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        position: "relative",
      }}
    >
      {/* Loading overlay */}
      {isLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <CircularProgress 
            size={60} 
            sx={{ 
              color: "#756DE9",
              marginBottom: 2,
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              color: "#333",
              fontWeight: 500,
            }}
          >
            Loading company details...
          </Typography>
        </Box>
      )}

      {!isLoading && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            padding: "20px",
            width: "60%",
            marginLeft: "15%",
          }}
          component="form"
          onSubmit={handleSubmit(onSubmit)}
        >
        <Typography style={{ fontSize: "18px", fontWeight: "600", color: "#1F2235", marginTop: "10px" }}>
          Company Details
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            width: "80%",
            backgroundColor: "#ECFFE9",
            padding: "10px",
            height: "10%",
            margin: "30px 0px 30px 0px",
            borderRadius: "10px",
          }}
        >
          <img src="/images/client-website/Vector-smile.png" alt="Connector" width="20px" height="20px" />
          <Typography style={{ fontSize: "14px", fontWeight: "400", color: "#00886C" }}>
            This information is required to fill only one time, and will appear with all job posts giving applicants a
            fair idea of the company.
          </Typography>
        </Box>

        <div className="avatar-upload">
          <div className="avatar-edit">
            <input
              capture={false}
              type="file"
              id="imageUpload"
              onChange={handleFileChange}
              accept=".png, .jpg, .jpeg"
            />
            <label htmlFor="imageUpload">
              <EditIcon style={{ fontSize: 20, color: "#827BEA" }} />
            </label>
          </div>
          <div className="avatar-preview">
            <div 
              id="imagePreview" 
              onClick={() => {
                if (previewUrl || logo) {
                  setPreviewModalOpen(true);
                }
              }}
              style={{ 
                backgroundImage: previewUrl || logo ? `url(${previewUrl || logo})` : 'none',
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundColor: previewUrl || logo ? 'transparent' : '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: '14px',
                cursor: previewUrl || logo ? 'pointer' : 'default',
                transition: 'opacity 0.2s',
              }}
              onMouseEnter={(e) => {
                if (previewUrl || logo) {
                  e.currentTarget.style.opacity = '0.8';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              {!previewUrl && !logo && 'No image selected'}
            </div>
          </div>
        </div>
        
        <Typography
          variant="caption"
          align="left"
          display="block"
          sx={{ 
            color: "#777777", 
            fontSize: "12px", 
            fontWeight: "400",
            marginTop: "8px",
            marginBottom: "10px"
          }}
        >
          Accepts only PNG and JPEG formats
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "20px", width: "81%" }}>
          <TextInputField 
            name="name" 
            label="Company Name *" 
            disabled={!canEditCompany()} 
            control={control} 
            placeholder="Enter the company name" 
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "row", gap: "20px" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "40%" }}>
            <TextInputField 
              disabled={!canEditCompany()} 
              name="foundedIn" 
              label="Company Founded In *" 
              control={control} 
              placeholder="Enter the year" 
            />
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "40%" }}>
            <TextInputField
              disabled={!canEditCompany()}
              name="noOfEmployees"
              label="Number of Employees *"
              control={control}
              placeholder="Enter the number of employees"
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", width: "82%" }}>
          <TextInputField  
            disabled={!canEditCompany()}
            name="headquarter" 
            label="Headquarter *" 
            control={control} 
            placeholder="Enter the headquarter location" 
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", width: "81%" }}>
          <InputLabel id="tools-label" shrink={true} sx={labelStylestextfield}>
            Industry the company operates in (Upto 3 industries) *
          </InputLabel>
          <MultiSelect 
             disabled={!canEditCompany()}
            name="industryOperateIn" control={control} options={getKeyValuePairs(industries)} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "20px", width: "81%" }}>
          <TextInputField
            disabled={!canEditCompany()}
            name="description"
            label="About the company *"
            control={control}
            placeholder="Enter the company description"
            isTextArea={true}
          />
        </Box>
        {/* Only visible to reviewers */}
        {/* {user?.user?.roleDetail?.roleName === "reviewer" && ( */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "20px", width: "81%" }}>
            <TextInputField
              type="number"
              disabled={!canEditCompany()  }
              name="usersLimit"
              label="Users Limit *"
              control={control}
              placeholder="Enter the users limit"
            />
          </Box>
        {/* )} */}

        <Typography
          variant="caption"
          align="left"
          display="block"
          sx={{ color: wordCount > maxWords ? "red" : "text.secondary", fontSize: "14px", fontWeight: "400" }}
        >
          {wordCount}/{maxWords} words
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "20px", width: "81%" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfieldsocial}>
              Company Website
            </InputLabel>
          </Stack>
          <Controller
            name="website"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  disabled={!canEditCompany()}
                  fullWidth
                  sx={{
                    boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                    borderRadius: "95px",
                    color: "#777777",
                  }}
                  placeholder="Enter the website URL"
                  error={!!error?.message}
                />
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                  {error ? error?.message : " "}
                </FormHelperText>
              </>
            )}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "81%" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src="/images/client-website/x-social-media.png" alt="icon" style={{ width: 16, height: 16 }} />
            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfieldsocial}>
              Link
            </InputLabel>
          </Stack>
          <Controller
            name="twitterProfile"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  disabled={!canEditCompany()}
                  sx={{
                    boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                    borderRadius: "95px",
                    color: "#777777",
                  }}
                  placeholder="Enter the Twitter profile URL"
                  error={!!error?.message}
                />
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                  {error ? error?.message : " "}
                </FormHelperText>
              </>
            )}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "81%" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src="/images/linkedin.png" alt="icon" style={{ width: 30, height: 30 }} />
            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfieldsocial}>
              Linkedin Link
            </InputLabel>
          </Stack>
          <Controller
            name="linkedinProfile"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  disabled={!canEditCompany()}
                  sx={{
                    boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                    borderRadius: "95px",
                    color: "#777777",
                  }}
                  placeholder="Enter the LinkedIn profile URL"
                  error={!!error?.message}
                />
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                  {error ? error?.message : " "}
                </FormHelperText>
              </>
            )}
          />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", width: "81%" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <img src="/images/client-website/instagram-original.png" alt="icon" style={{ width: 16, height: 16 }} />
            <InputLabel id="tools-label" shrink={true} sx={labelStylestextfieldsocial}>
              Instagram Link
            </InputLabel>
          </Stack>
          <Controller
            name="facebookProfile"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <TextField
                  {...field}
                  fullWidth
                  disabled={!canEditCompany()}
                  sx={{
                    boxShadow: "0px 0px 11px 2px #4B44D41A inset",
                    borderRadius: "95px",
                    color: "#777777",
                  }}
                  placeholder="Enter the Instagram profile URL"
                  error={!!error?.message}
                />
                <FormHelperText sx={{ color: "#d32f2f", mt: 0, ml: "14px" }}>
                  {error ? error?.message : " "}
                </FormHelperText>
              </>
            )}
          />
        </Box>
        {/* {edit && ( */}
        <Box sx={{ display: "flex", marginTop: "20px", gap: "20px" }}>
          {canEditCompany() && (
            <Button
              type="submit"
              variant="outlined"
              sx={{
                backgroundColor: "#756DE9",
                color: "#fff",
                borderRadius: "100px",
                padding: "10px 20px",
                "&:hover": {
                  backgroundColor: "#5a4dbf",
                  color: "#fff",
                  borderColor: "#5a4dbf",
                },
              }}
              disabled={!!ajaxCallsInProgress}
            >
              Save Changes
            </Button>
          )}
        </Box>
        {/* )} */}
        </Box>
      )}

      {/* Image Preview Modal */}
      <Modal
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            backgroundColor: '#fff',
            borderRadius: '12px',
            padding: '20px',
            outline: 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setPreviewModalOpen(false)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: '#fff',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
          <img
            src={previewUrl || logo || ''}
            alt="Company Logo Preview"
            style={{
              maxWidth: '100%',
              maxHeight: '80vh',
              objectFit: 'contain',
              borderRadius: '8px',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              marginTop: 2,
              color: '#666',
              fontSize: '14px',
            }}
          >
            Company Logo Preview
          </Typography>
        </Box>
      </Modal>
    </Box>
  );
};

export default CompanyDetails;
